import { Observable, of, Subject } from 'rxjs';
import { BrowserDbService } from './browser-db.service';
import { MmProjectDbSchema } from './mm-project-db-schema';
import { filter, map, catchError, shareReplay, take, switchMap } from 'rxjs/operators';
import { User, AuthService } from '@mydocma/auth';
import { openCriticalErrorDialog } from '@mydocma/component-library/src/lib/critical-error-dialog';
import { Injectable } from '@angular/core';
import { IDBPDatabase, IDBPTransaction } from 'idb';
import { IdbMigratorService } from '@mydocma/core/src/lib/migration';
import { projectDbMigration, dbMigration } from './migration/version-to-migration';
import { ProjectService, Project } from '@mydocma/project';
import { MmDbSchema } from './mm-db-schema';
import {MatLegacyDialog as MatDialog} from '@angular/material/legacy-dialog';
import { Logger } from '@mydocma/common/src/lib/logger';

@Injectable()
export class MmDbService {

  static readonly mmProjectDbName = 'mydocma/mm/:userId/:projectId';
  static readonly mmDbName = 'mydocma/mm/:userId';

    readonly currentUser$: Observable<User> = this.authService.user
    .pipe(
      filter(Boolean),
      catchError(e => {
        // also handles errors bubbled up from BrowserDbService
        this.logger.error(e, '', 'MmDbService');
        return of(null);
      }),
      shareReplay(),
    );

  constructor(private readonly authService: AuthService,
              private readonly projectService: ProjectService,
              private readonly migratorService: IdbMigratorService,
              private readonly matDialog: MatDialog,
              private readonly logger: Logger) {

  }


  private mapProjectDb(user: User, projectId: string): BrowserDbService<MmProjectDbSchema> {

    const userId = user && user.id;

    if (!userId) {
      console.debug(`Aborting opening db. Missing User ID(${userId})`);
      return null;
    }

    if (!projectId) {
      console.debug(`Aborting opening db. Missing Project ID(${projectId})`);
      return null;
    }
    const upgradeFinished$ = new Subject<void>();
    const upgradeFinished = upgradeFinished$.pipe(take(1)).toPromise();

    const upgrade = async (
      database: IDBPDatabase<unknown>,
      oldVersion: number,
      newVersion: number | null,
      transaction: IDBPTransaction<unknown>,
    ) => {
      await this.migratorService.migrate(
        oldVersion,
        newVersion,
        database,
        transaction,
        projectDbMigration,
      );
      upgradeFinished$.next();
    };



    return BrowserDbService.getDb<MmProjectDbSchema>(
      MmDbService.mmProjectDbName.replace(':userId', userId).replace(':projectId', projectId),
      19,
      {
        upgrade,
      },
      upgradeFinished
    );
  }



  private mapDb(user: User): BrowserDbService<MmDbSchema> {

    const userId = user && user.id;

    if (!userId) {
      console.debug(`Aborting opening db. Missing User ID(${userId})`);
      return null;
    }

    const upgradeFinished$ = new Subject<void>();
    const upgradeFinished = upgradeFinished$.pipe(take(1)).toPromise();

    const upgrade = async (
      database: IDBPDatabase<unknown>,
      oldVersion: number,
      newVersion: number | null,
      transaction: IDBPTransaction<unknown>,
    ) => {
      await this.migratorService.migrate(
        oldVersion,
        newVersion,
        database,
        transaction,
        dbMigration,
      );
      upgradeFinished$.next();
    };



    return BrowserDbService.getDb<MmDbSchema>(
      MmDbService.mmDbName.replace(':userId', userId),
      1,
      {
        upgrade,
      },
      upgradeFinished
    );
  }


  getProjectDb(projectId: string): Promise<BrowserDbService<MmProjectDbSchema>> {
    return this.currentUser$
      .pipe(
        map(u => this.mapProjectDb(u, projectId) ),
        take(1),
      ).toPromise()
      .catch(() => {
        this.handleGetDbError();
        return null;
      }) as Promise<BrowserDbService<MmProjectDbSchema>>;
  }

  getDb(): Promise<BrowserDbService<MmDbSchema>> {
    return this.currentUser$
      .pipe(
        map(u => this.mapDb(u) ),
        take(1),
      ).toPromise()
      .catch(() => {
        this.handleGetDbError();
        return null;
      }) as Promise<BrowserDbService<MmDbSchema>>;
  }

  handleGetDbError(data?: any ) {
    openCriticalErrorDialog(this.matDialog, data ? {data} : { data: { title: 'Indexeddb doesn\'t work in your browser' } });
  }

}
